import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import ReportService from '../../services/ReportService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);

export default function StockReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const toast = useRef(null);
    const dt = useRef(null);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await ReportService.getStockReport();
            setData(res.data.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Rapor alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const stockBodyTemplate = (rowData) => {
        const isCritical = rowData.stockQuantity <= 5;
        const isLow = rowData.stockQuantity > 5 && rowData.stockQuantity <= 20;

        return (
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    <span className={`text-sm font-black ${isCritical ? 'text-rose-600' : isLow ? 'text-amber-600' : 'text-slate-700'}`}>
                        {rowData.stockQuantity}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{rowData.unit}</span>
                </div>
                {isCritical && <Tag value="KRİTİK STOK" severity="danger" className="text-[8px] font-black px-2 py-0.5 rounded-md shadow-sm" />}
                {isLow && <Tag value="DÜŞÜK STOK" severity="warning" className="text-[8px] font-black px-2 py-0.5 rounded-md shadow-sm" />}
            </div>
        );
    };

    const totalCostValue = data.reduce((acc, r) => acc + (r.stockQuantity * r.costPrice), 0);
    const totalSaleValue = data.reduce((acc, r) => acc + (r.stockQuantity * r.salePrice), 0);
    const potentialProfit = totalSaleValue - totalCostValue;

    const header = (
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 py-2">
            <div className="flex items-center gap-3 w-full xl:w-auto">
                 <Button icon="pi pi-refresh" onClick={fetchReport} loading={loading} className="p-button-rounded p-button-secondary bg-white border-slate-200 text-slate-600 shadow-sm" />
                 <h2 className="text-sm font-black text-slate-500 uppercase tracking-widest ml-1">Stok Detay Listesi</h2>
            </div>
            <div className="flex items-center gap-3 w-full xl:w-auto mt-2 xl:mt-0">
                <span className="p-input-icon-left flex-1 xl:flex-none">
                    <i className="pi pi-search text-slate-400 pl-2" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Ürün veya barkod ara..." className="w-full p-5 !py-2 bg-slate-50 border-slate-200 rounded-xl font-bold text-sm h-[40px]" />
                </span>
                <Button type="button" icon="pi pi-file-excel" label="EXCEL" severity="success" onClick={exportCSV} className="!bg-emerald-600 border-none rounded-xl px-4 h-[40px] font-black text-[11px] text-white" />
            </div>
        </div>
    );

    return (
        <div className="p-2 md:p-2  bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            {/* Elite Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] !bg-indigo-600 flex items-center justify-center shadow-2xl shadow-indigo-400">
                        <i className="pi pi-box text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Stok Raporu</h1>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Envanter Değeri ve Kritik Seviyeler</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-4">
                <div className="!bg-slate-900 p-4 rounded-[2.5rem] border border-slate-800 shadow-xl shadow-slate-900/20 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[14px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Toplam Stok Değeri</p>
                        <h3 className="text-3xl font-black text-white tracking-tighter">{fmtMoney(totalCostValue)}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl !bg-slate-600 shadow-sm flex items-center justify-center !text-blue-400">
                        <i className="pi pi-database text-2xl" />
                    </div>
                </div>
                <div className="!bg-emerald-100 p-4 rounded-[2.5rem] border border-white/50 shadow-xl shadow-emerald-500/10 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[14px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-2">Beklenen Satış</p>
                        <h3 className="text-3xl font-black text-emerald-600 tracking-tighter">{fmtMoney(totalSaleValue)}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl !bg-white shadow-sm flex items-center justify-center !text-emerald-600">
                        <i className="pi pi-shopping-bag text-2xl" />
                    </div>
                </div>
                <div className="!bg-blue-200 p-4 rounded-[2.5rem] border border-white/50 shadow-xl !shadow-blue-500/10 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[14px] font-black text-blue-600 uppercase tracking-[0.2em] mb-2">Potansiyel Brüt Kar</p>
                        <h3 className="text-3xl font-black text-blue-600 tracking-tighter">{fmtMoney(potentialProfit)}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center !text-blue-600">
                        <i className="pi pi-chart-pie text-2xl" />
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                <div className=" p-2 md:p-4">
                    <DataTable
                        ref={dt}
                        value={data}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={['productName', 'barcode', 'categoryName']}
                        header={header}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[15, 30, 50]}
                        className="p-datatable-sm"
                        emptyMessage="Kayıt bulunamadı."
                        removableSort
                        rowClassName={() => "hover:bg-slate-50/80 transition-colors"}
                    >
                        <Column field="productName" header="ÜRÜN ADI" body={(r) => (
                             <div className="flex flex-col">
                                <span className="font-black text-slate-700">{r.productName}</span>
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{r.barcode || 'Barkodsuz'}</span>
                             </div>
                        )} sortable headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="categoryName" header="KATEGORİ" body={(r) => (
                             <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">{r.categoryName}</span>
                        )} sortable headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="stockQuantity" header="STOK" body={stockBodyTemplate} sortable headerClassName="bg-slate-100 text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="costPrice" header="MALİYET" body={(r) => <span className="font-bold text-slate-600">{fmtMoney(r.costPrice)}</span>} sortable headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column header="TOPLAM MALİYET" body={(r) => <span className="font-black text-slate-800 bg-slate-50 px-3 py-1 rounded-lg">{fmtMoney(r.stockQuantity * r.costPrice)}</span>} headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="salePrice" header="SATIŞ FİYATI" body={(r) => <span className="font-bold text-emerald-600">{fmtMoney(r.salePrice)}</span>} sortable headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column header="POTANSİYEL GELİR" body={(r) => <span className="font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg">{fmtMoney(r.stockQuantity * r.salePrice)}</span>} headerClassName="bg-slate-100  text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
